<template>
  <div class="task-details-page">
    <Header ref="header" helpURL="studio/tasks/">
      <template #page-slot>
        {{ pageTitle }}
      </template>
      <template #header-actions>
        <IconButton
          icon="arrow-left"
          :width="24"
          :height="24"
          class="back-button"
          @click="goBack"
          :title="backButtonTitle"
        />
      </template>
    </Header>
    <div class="main-page-div">
      <main class="main-display-area">
        <div v-if="loading" class="loading-state">
          <div class="loading-card" role="status" aria-live="polite">
            <div class="loader-ring" />
            <div class="loading-title">{{ loadingMessage }}</div>
            <div class="loading-subtitle">
              Please wait<span class="loading-dots"><span>.</span><span>.</span><span>.</span></span>
            </div>
          </div>
        </div>
        
        <div v-else>
          <!-- Task Card Section -->
          <div v-if="showTaskCard" class="task-card-section">
            <TaskCard 
              :session="task"
              :is-list="isListView"
              :wide="true"
            />
            <div class="view-toggle">
              <button 
                class="view-toggle-btn"
                :class="{ active: true }"
                title="Toggle Task Details"
                @click="toggleListView"
              >
                <v-icon>{{ isListView ? 'mdi-chevron-down' : 'mdi-chevron-up' }}</v-icon>
              </button>
            </div>
            <div class="tab-buttons">
              <SelectCharts 
                v-if="chartGroups && chartGroups.length > 0"
                v-model:group="selectedGroup"
                :task-id="taskID"
                :chart-groups="chartGroups"
                @switch-tab="activeTab = $event"
                @reload-charts="handleReloadCharts"
              />
              <SelectTrace 
                v-if="traceList && traceList.length > 0"
                :task-id="taskID"
                :trace-options="traceList"
                :trace-file="selectedTrace"
                @open-trace="handleOpenTrace"
                @switch-tab="activeTab = $event"
                @reload-trace="handleReloadTrace"
              />
            </div>
          </div>

          <!-- Main Content Area -->
          <div
            class="content-area"
            :class="{ 'content-area--profiler': useProfileCharts && activeTab === 'charts' }"
            :style="{ height: availableHeight }"
          >
            <div v-if="useProfileCharts && activeTab === 'charts'" class="profiler-layout">
              <section class="platformDiv">
                <div class="panel-card">
                  <div class="panel-header">Sessions</div>
                  <div class="panel-body">
                    <ValiListMini
                      :sessions="profileValidationSessions"
                      :empty-text="'No validation sessions attached.'"
                      :max-mode="true"
                    />
                  </div>
                </div>
              </section>
              <section class="chartsDiv">
                <div class="panel-card panel-card--fill">
                  <div class="panel-header panel-header--with-action">
                    <span>Charts</span>
                    <button
                      type="button"
                      class="panel-action-btn"
                      :disabled="isUpdatingCharts"
                      @click="handleUpdateCharts"
                    >
                      <v-icon size="16">mdi-refresh</v-icon>
                      <span>{{ isUpdatingCharts ? 'Updating...' : 'Update Charts' }}</span>
                    </button>
                  </div>
                  <div class="panel-body panel-body--fill">
                    <Charts
                      :task-id="taskID"
                      :group-name="selectedGroup"
                      :chart-file-names="chartFileNames"
                      :chart-items="profileChartItems"
                      @switch-tab="activeTab = $event"
                    />
                  </div>
                </div>
              </section>
            </div>
            <Charts
              v-else-if="activeTab === 'charts'"
              :task-id="taskID"
              :group-name="selectedGroup"
              :chart-file-names="chartFileNames"
              :chart-items="profileChartItems"
              @switch-tab="activeTab = $event"
            />
            <Profiler v-else-if="activeTab === 'profile'" :task-id="taskID" :trace-file="selectedTrace" />
          </div>
        </div>
      </main>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';
import Header from '@/components/Header.vue';
import IconButton from '@/components/IconButton.vue';
import TaskCard from '@/components/Apps/TaskCard.vue';
import SelectCharts from '@/components/ProfilerComponents/SelectCharts.vue';
import SelectTrace from '@/components/ProfilerComponents/SelectTrace.vue';
import Charts from '@/components/ProfilerComponents/Charts.vue';
import Profiler from '@/components/ProfilerComponents/Profiler.vue';
import ValiListMini from '@/components/ValidationPage/ValiListMini.vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import { decodeID } from '@/assets/js/utils.js';

const props = defineProps({
  pageTitle: {
    type: String,
    default: 'Task Details',
  },
  showTaskCard: {
    type: Boolean,
    default: true,
  },
  useProfileCharts: {
    type: Boolean,
    default: false,
  },
  backPath: {
    type: String,
    default: '/tasks',
  },
  backButtonTitle: {
    type: String,
    default: 'Back to Tasks',
  },
});

const router = useRouter();
const route = useRoute();
const $toast = useToast();

const taskUID = ref('');
const taskID = ref(null);
const loading = ref(true);
const task = ref(null);
const activeTab = ref('charts');
const chartGroups = ref([]);
const chartList = ref({});
const traceList = ref([]);
const selectedGroup = ref('');
const selectedTrace = ref('');
const chartFileNames = ref([]);
const profileChartItems = ref([]);
const isUpdatingCharts = ref(false);
const isListView = ref(true);
const loadingMessage = computed(() => (props.useProfileCharts ? 'Loading Benchmarking Results' : 'Loading task details...'));
const profileValidationSessions = computed(() => {
  const profile = task.value || {};

  if (Array.isArray(profile.val_sessions)) {
    return profile.val_sessions;
  }

  if (Array.isArray(profile.vals_sessions)) {
    return profile.vals_sessions;
  }

  if (Array.isArray(profile.val_session)) {
    return profile.val_session;
  }

  if (Array.isArray(profile.params?.val_sessions)) {
    return profile.params.val_sessions;
  }

  if (Array.isArray(profile.params?.vals_sessions)) {
    return profile.params.vals_sessions;
  }

  return [];
});

// Refs for height calculation
const windowHeight = ref(window.innerHeight);
const calculatedHeight = ref('100%');

// Function to calculate available height with fixed dimensions
const calculateAvailableHeight = () => {
  const headerHeight = 70;
  const taskCardHeight = props.showTaskCard ? 70 : 0;
  const availH = windowHeight.value - headerHeight - taskCardHeight;
  
  if (availH > 0) {
    calculatedHeight.value = `${availH}px`;
    console.log(`✅ Height calculated: ${calculatedHeight.value} (window: ${windowHeight.value} - header: ${headerHeight} - card: ${taskCardHeight})`);
  }
};

const availableHeight = computed(() => calculatedHeight.value);

const goBack = () => {
  router.push(props.backPath);
};

const toggleListView = () => {
  console.log('List view toggled');
  isListView.value = !isListView.value;
};

const fetchTaskData = async () => {
  try {
    loading.value = true;
    
    // Decode UID to numeric task ID (handles bt-, v-, t-, ds- prefixes)
    taskID.value = decodeID(taskUID.value);

    if (!taskID.value) {
      throw new Error(`Invalid task UID: ${taskUID.value}`);
    }
    
    const dataConnect = new DatastoreConnect();

    if (!props.useProfileCharts) {
      const response = await dataConnect.api('task.get', { id: taskID.value });

      if (!response || response.error || !response.result) {
        throw new Error(response?.error || 'Failed to fetch task data');
      }

      task.value = response.result;

      // Fetch chart groups
      await fetchChartGroups();

      // Fetch trace list
      await fetchTraceList();
    } else {
      task.value = {
        id: taskID.value,
        uid: taskUID.value,
        status: '',
        type: 'Profiler',
      };
      await fetchProfileDetails(dataConnect);
      await fetchProfileCharts();
    }
  } catch (error) {
    console.error('Error fetching task data:', error);
    
    // Show error toast
    $toast.open({
      queue: true,
      dismissible: true,
      message: error.message || 'Failed to fetch task details',
      type: 'error',
      position: 'top',
    });
    
    // Fallback data on error
    task.value = {
      id: taskUID.value,
      uid: taskUID.value,
      status: 'Error',
      type: 'Unknown',
    };
  } finally {
    loading.value = false;
  }
};

const fetchProfileDetails = async (dataConnect = new DatastoreConnect()) => {
  try {
    const response = await dataConnect.api('profile.get', { id: taskID.value });
    const profile = response?.result?.task || response?.result?.profile || response?.result;
    if (profile && typeof profile === 'object' && !Array.isArray(profile)) {
      task.value = profile;
    }
  } catch (error) {
    console.error('Error fetching profile details:', error);
  }
};

const fetchChartGroups = async () => {
  try {
    const dataConnect = new DatastoreConnect();
    const response = await dataConnect.api('task.chart.list', {
      task_id: taskID.value,
    });
    
    if (response && response.result) {
      // Extract chart group names from response
      chartList.value = response.result.data || {};
      chartGroups.value = Object.keys(chartList.value);
    }
  } catch (error) {
    console.error('Error fetching chart groups:', error);
  }
};

const fetchProfileCharts = async () => {
  try {
    const dataConnect = new DatastoreConnect();
    const response = await dataConnect.api('profile.charts', {
      profile_id: taskID.value,
    });

    const payload = response?.result?.data
      || response?.result?.charts
      || response?.result
      || {};

    if (Array.isArray(payload)) {
      profileChartItems.value = payload
        .filter((item) => item && typeof item === 'object')
        .map((item, index) => {
          const maybePayload = item.data && typeof item.data === 'object' ? item.data : item;
          const title = item.name || item.chart_name || maybePayload?.meta?.title || `Chart ${index + 1}`;

          if (!maybePayload.meta || typeof maybePayload.meta !== 'object') {
            maybePayload.meta = {};
          }
          if (!maybePayload.meta.title) {
            maybePayload.meta.title = title;
          }

          return {
            title,
            payload: maybePayload,
          };
        });

      chartList.value = {};
      chartGroups.value = [];
      chartFileNames.value = [];
      return;
    }

    profileChartItems.value = [];
    if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
      chartList.value = payload;
      chartGroups.value = Object.keys(payload);
      if (chartGroups.value.length > 0) {
        selectedGroup.value = selectedGroup.value || chartGroups.value[0];
        chartFileNames.value = payload[selectedGroup.value] || [];
      } else {
        chartFileNames.value = [];
      }
      return;
    }

    chartList.value = {};
    chartGroups.value = [];
    chartFileNames.value = [];
    profileChartItems.value = [];
  } catch (error) {
    console.error('Error fetching profile charts:', error);
    profileChartItems.value = [];
  }
};

const fetchTraceList = async () => {
  try {
    const dataConnect = new DatastoreConnect();
    const response = await dataConnect.api('task.data.list', {
      task_id: taskID.value,
    });
    
    if (response && response.result) {
      // response.result is an array of trace file names
      traceList.value = response.result.traces;
    }
  } catch (error) {
    console.error('Error fetching trace list:', error);
  }
};



const handleOpenTrace = (data) => {
  selectedTrace.value = data.trace;
  console.log('Opening trace:', data);
};

const handleReloadTrace = (data) => {
  // Re-trigger trace loading by resetting and reloading
  handleOpenTrace(data);
  console.log('Reloading trace:', data);
};

const handleUpdateCharts = async () => {
  if (!props.useProfileCharts || !taskID.value || isUpdatingCharts.value) {
    return;
  }

  try {
    isUpdatingCharts.value = true;
    const dataConnect = new DatastoreConnect();
    await dataConnect.api('profile.update', {
      id: taskID.value,
    });

    await fetchProfileCharts();
  } catch (error) {
    console.error('Error updating profile charts:', error);
    $toast.open({
      queue: true,
      dismissible: true,
      message: error?.message || 'Failed to update charts',
      type: 'error',
      position: 'top',
    });
  } finally {
    isUpdatingCharts.value = false;
  }
};

const handleReloadCharts = async () => {
  if (props.useProfileCharts) {
    await fetchProfileCharts();
    return;
  }

  if (selectedGroup.value && taskID.value) {
    try {
      // Clear charts first to force re-render
      chartFileNames.value = [];
      
      const dataConnect = new DatastoreConnect();
      const response = await dataConnect.api('task.chart.list', {
        task_id: taskID.value,
        group: selectedGroup.value,
      });
      
      if (response?.result?.data && response.result.data[selectedGroup.value]) {
        // Create new array to ensure Vue detects the change
        chartFileNames.value = [...response.result.data[selectedGroup.value]];
        console.log('✅ Charts reloaded for group:', selectedGroup.value, chartFileNames.value);
      }
    } catch (error) {
      console.error('Error reloading charts:', error);
    }
  }
};

function parseMaybeJson(value) {
  if (typeof value !== 'string') {
    return value;
  }

  const trimmed = value.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
    return value;
  }

  try {
    return JSON.parse(trimmed);
  } catch (error) {
    return value;
  }
}


// Watch for loading change to recalculate after content renders
watch(
  () => loading.value,
  (isLoading) => {
    if (!isLoading) {
      calculateAvailableHeight();
    }
  }
);

// Watch for changes to selectedGroup
watch(
  () => selectedGroup.value,
  async (newGroup) => {
    if (newGroup && taskID.value) {
      console.log('📥 Chart group changed:', newGroup);
      chartFileNames.value = chartList.value[newGroup] || [];
    }
  }
);

onMounted(() => {
  // Get task UID from route params
  taskUID.value = route.params.task_uid;
  fetchTaskData();
  
  // Handle window resize events
  const handleResize = () => {
    windowHeight.value = window.innerHeight;
    calculateAvailableHeight();
  };
  window.addEventListener('resize', handleResize);
  
  // Calculate height after initial render
  calculateAvailableHeight();
  
  return () => window.removeEventListener('resize', handleResize);
});
</script>

<style lang="scss" scoped>
.task-details-page {
  display: flex;
  flex-direction: column;
  @include full-vh;
}

.main-page-div {
  flex: 1;
  overflow: hidden;
}

.main-display-area {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
  @include themify() {
    background: themed('page-background');
  }
}

// Header styling
::v-deep header {
  height: 70px;
}

.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  padding: 24px;

  .loading-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 24px 28px;
    border-radius: 12px;
    min-width: 260px;

    @include themify() {
      background: themed('input-background');
      border: 1px solid themed('border-color');
    }
  }

  .loader-ring {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    border: 4px solid rgba(var(--v-theme-primary), 0.2);
    border-top-color: rgb(var(--v-theme-primary));
    animation: spin-loader 0.9s linear infinite;
  }

  .loading-title {
    font-size: 18px;
    font-weight: 700;
    text-align: center;

    @include themify() {
      color: themed('body-text-color');
    }
  }

  .loading-subtitle {
    font-size: 13px;
    text-align: center;

    @include themify() {
      color: themed('body-text-color');
    }
  }

  .loading-dots {
    display: inline-flex;
    min-width: 20px;
  }

  .loading-dots span {
    opacity: 0.2;
    animation: dot-fade 1.2s ease-in-out infinite;
  }

  .loading-dots span:nth-child(2) {
    animation-delay: 0.2s;
  }

  .loading-dots span:nth-child(3) {
    animation-delay: 0.4s;
  }
}

@keyframes spin-loader {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

@keyframes dot-fade {
  0%,
  80%,
  100% {
    opacity: 0.2;
  }

  40% {
    opacity: 1;
  }
}

// Task Card Section
.task-card-section {
  display: flex;
  gap: 12px;
  flex-shrink: 0;
  padding-left: 10px;
  padding-right: 10px;

  height: 70px;


}

.task-card-wrapper {
  flex: 1;
  min-width: 0;
}

.tab-buttons {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  margin-left: auto;
  padding-bottom: 12px;
  @include themify() {
    border-bottom-color: themed('border-color');
  }
}

.tab-button {
  padding: 10px 16px;
  border: 2px solid #ccc;
  border-radius: 6px;
  background: white;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  white-space: nowrap;
  @include themify() {
    color: themed('body-text-color');
    border-color: themed('border-color');
    background: themed('input-background');
  }

  &:hover:not(.active) {
    border-color: #2196f3;
    background: #f5f5f5;
  }

  &.active {
    background: #2196f3;
    color: white;
    border-color: #2196f3;
  }
}

// Content Area
.content-area {
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
  @include themify() {
    background: themed('card-color');
  }
}

.content-area--profiler {
  overflow: hidden;
}

.profiler-layout {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  height: 100%;
  min-height: 0;
  padding: 16px;
}

.platformDiv,
.chartsDiv {
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.platformDiv {
  flex: 0 1 calc(36% - 10px);
  min-width: 300px;
  max-width: 576px;
}

.chartsDiv {
  flex: 1 1 calc(64% - 10px);
  min-width: 420px;
}

.panel-card {
  display: flex;
  flex-direction: column;
  min-height: 100%;
  height: 100%;
  border-radius: 12px;
  padding: 16px;
  box-sizing: border-box;
  @include themify() {
    background: themed('input-background');
    border: 1px solid themed('border-color');
  }
}

.panel-card--fill {
  height: 100%;
}

.panel-header {
  flex-shrink: 0;
  margin-bottom: 8px;
  padding-bottom: 6px;
  border-bottom: 1px solid #e2e8f0;
  font-size: 18px;
  font-weight: 700;
  @include themify() {
    color: themed('body-text-color');
    border-bottom-color: themed('border-color');
  }
}

.panel-header--with-action {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.panel-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 10px;
  border: 1px solid rgb(var(--v-theme-primary));
  border-radius: 6px;
  background: rgb(var(--v-theme-primary));
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    filter: brightness(0.92);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.panel-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}

.panel-body--fill {
  display: flex;
  flex: 1;
  min-height: 0;
}

.panel-body--fill :deep(.charts-container) {
  display: flex;
  flex: 1;
  height: 100%;
  min-height: 0;
}

.panel-body--fill :deep(.charts-grid) {
  flex: 1;
  min-height: 0;
}

.platform-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.panel-empty-state {
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  border-radius: 12px;
  @include themify() {
    color: themed('body-text-color-secondary');
    background: themed('color-white-100');
    border: 1px dashed themed('color-primary-200');
  }
}

@media (max-width: 1100px) {
  .platformDiv,
  .chartsDiv {
    flex-basis: 100%;
    max-width: none;
    min-width: 0;
  }

  .platformDiv {
    max-height: 40%;
  }
}

.status-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;

  &.active {
    background-color: #4caf50;
    color: white;
  }

  &.completed {
    background-color: #2196f3;
    color: white;
  }

  &.failed {
    background-color: #f44336;
    color: white;
  }

  &.pending {
    background-color: #ff9800;
    color: white;
  }
}

.empty-message {
  padding: 32px;
  text-align: center;
  @include themify() {
    color: themed('body-text-color-secondary');
  }
}

.back-button {
  cursor: pointer;
}

.view-toggle {
  display: flex;
  gap: 8px;
  align-items: center;
}

.view-toggle-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: 1px solid #ccc;
  background: white;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  color: #333;

  &:hover {
    background: #f5f5f5;
    border-color: #999;
  }

  &.active {
    background: #0066cc;
    border-color: #0066cc;
    color: white;
  }
}

</style>
